/**
 * Página de gestión de cursos del profesor
 */

import React, { useEffect, useState } from "react";
import { Notification } from "../../../components/Notification";
import { useAuth } from "../../../contexts/AuthContext";
import { cursosProfesorService } from "../../../services/profesor/cursos.service";
import { CursosTable, ProfesorLayout } from "../components";
import { CursoProfesor } from "./dto/cursos.dto";

export const ProfesorCursosPage: React.FC = () => {
  const { user } = useAuth();
  const [cursos, setCursos] = useState<CursoProfesor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroVigencia, setFiltroVigencia] = useState<
    "todos" | "vigente" | "no-vigente"
  >("vigente");
  const [mostrarModalCrear, setMostrarModalCrear] = useState(false);
  const [aniosEscolares, setAniosEscolares] = useState<
    { idAnioEscolar: number; nombre: string; orden: number }[]
  >([]);
  const [formData, setFormData] = useState({
    nombre: "",
    anio: new Date().getFullYear(),
    anioEscolarId: "",
    vigente: true,
  });
  const [creando, setCreando] = useState(false);
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    type: "success" | "error" | "info" | "warning";
    title: string;
    message: string;
  } | null>(null);

  useEffect(() => {
    if (user) {
      loadCursos();
      loadAniosEscolares();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadCursos = async () => {
    try {
      setLoading(true);
      const data = await cursosProfesorService.getCursos();
      setCursos(data);
    } catch (error) {
      console.error("Error cargando cursos:", error);
      setCursos([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAniosEscolares = async () => {
    try {
      const data = await cursosProfesorService.getAniosEscolares();
      setAniosEscolares(data);
    } catch (error) {
      console.error("Error cargando años escolares:", error);
      setAniosEscolares([]);
    }
  };

  const handleCrearCurso = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.nombre.trim() || !formData.anioEscolarId) {
      setNotification({
        isOpen: true,
        type: "warning",
        title: "Campos requeridos",
        message: "Debes completar todos los campos obligatorios",
      });
      return;
    }

    setCreando(true);
    try {
      await cursosProfesorService.createCurso({
        nombre: formData.nombre.trim(),
        anio: formData.anio,
        anioEscolarId: parseInt(formData.anioEscolarId),
        vigente: formData.vigente,
      });

      setNotification({
        isOpen: true,
        type: "success",
        title: "Curso creado",
        message: "El curso se ha creado exitosamente",
      });

      setMostrarModalCrear(false);
      setFormData({
        nombre: "",
        anio: new Date().getFullYear(),
        anioEscolarId: "",
        vigente: true,
      });
      loadCursos();
    } catch (error: any) {
      console.error("Error creando curso:", error);
      setNotification({
        isOpen: true,
        type: "error",
        title: "Error",
        message: error.response?.data?.error || "Error al crear el curso",
      });
    } finally {
      setCreando(false);
    }
  };

  const filteredCursos = cursos.filter((curso) => {
    const matchSearch =
      !searchTerm ||
      curso.nombreCompleto.toLowerCase().includes(searchTerm.toLowerCase()) ||
      curso.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      curso.colegio.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      curso.anioEscolar.toLowerCase().includes(searchTerm.toLowerCase());

    const matchVigencia =
      filtroVigencia === "todos" ||
      (filtroVigencia === "vigente" ? curso.vigente : !curso.vigente);

    return matchSearch && matchVigencia;
  });

  const sortedCursos = [...filteredCursos].sort((a, b) => {
    const extractNivelNumber = (anioEscolar: string): number => {
      const match = anioEscolar.match(/^(\d+)/);
      return match ? parseInt(match[1], 10) : 999;
    };

    const nivelA = extractNivelNumber(a.anioEscolar);
    const nivelB = extractNivelNumber(b.anioEscolar);

    if (nivelA !== nivelB) {
      return nivelA - nivelB;
    }

    return a.nombre.localeCompare(b.nombre);
  });

  return (
    <ProfesorLayout>
      <div className="w-full py-8">
        <div className="space-y-8 px-4 sm:px-6 lg:px-8">
          {/* Header con gradiente */}
          <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-purple-800 rounded-2xl shadow-2xl p-8 text-white">
            <div className="flex items-center gap-4 mb-4">
              <div className="h-16 w-16 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-3xl font-bold">Mis Cursos</h1>
                <p className="text-purple-100 mt-1">
                  Cursos asignados y cantidad de alumnos por curso
                </p>
              </div>
            </div>
            <p className="text-purple-50 text-sm">
              Administra tus cursos y visualiza la información de los
              estudiantes asignados
            </p>
          </div>

          {/* Botón crear curso */}
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setMostrarModalCrear(true)}
              className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-sm hover:shadow-md flex items-center gap-2 font-medium"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Crear Curso
            </button>
          </div>

          {/* Barra de búsqueda y filtros */}
          <div className="bg-white rounded-xl shadow-lg border border-purple-100 p-6">
            <div className="space-y-4">
              {/* Búsqueda */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Buscar Curso
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar por nombre, colegio o nivel..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                  />
                </div>
              </div>

              {/* Filtros */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vigencia
                </label>
                <select
                  value={filtroVigencia}
                  onChange={(e) =>
                    setFiltroVigencia(
                      e.target.value as "todos" | "vigente" | "no-vigente"
                    )
                  }
                  className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white"
                >
                  <option value="todos">Todos</option>
                  <option value="vigente">Vigente</option>
                  <option value="no-vigente">No vigente</option>
                </select>
              </div>
            </div>
          </div>

          {/* Tabla de cursos */}
          <div className="w-full overflow-x-auto">
            <div className="bg-white rounded-xl shadow-lg border border-purple-100 inline-block min-w-full">
              <CursosTable
                cursos={sortedCursos}
                onRefresh={loadCursos}
                loading={loading}
              />
            </div>
          </div>

          {/* Info */}
          {!loading && (
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg border border-purple-200">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-sm font-medium">
                  Mostrando {sortedCursos.length} de {cursos.length} cursos
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal crear curso */}
      {mostrarModalCrear && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setMostrarModalCrear(false)}
          ></div>

          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full">
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex items-center justify-between rounded-t-2xl">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-white">
                    Crear Nuevo Curso
                  </h2>
                </div>
                <button
                  onClick={() => setMostrarModalCrear(false)}
                  className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCrearCurso} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nombre del Curso *
                  </label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) =>
                      setFormData({ ...formData, nombre: e.target.value })
                    }
                    placeholder="Ej: A, B, C, 1, 2, 3..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Letra o número que identifica el curso (ej: A, B, 1, 2)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Año Escolar *
                  </label>
                  <select
                    value={formData.anioEscolarId}
                    onChange={(e) =>
                      setFormData({ ...formData, anioEscolarId: e.target.value })
                    }
                    disabled={aniosEscolares.length === 0}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                    required
                  >
                    {aniosEscolares.length === 0 ? (
                      <option value="">No hay registros disponibles</option>
                    ) : (
                      <>
                        <option value="">Seleccione un año escolar</option>
                        {aniosEscolares.map((anio) => (
                          <option
                            key={anio.idAnioEscolar}
                            value={anio.idAnioEscolar}
                          >
                            {anio.nombre}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Año *
                  </label>
                  <input
                    type="number"
                    value={formData.anio}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        anio: parseInt(e.target.value) || new Date().getFullYear(),
                      })
                    }
                    min="1900"
                    max="2100"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Año académico del curso (ej: 2025, 2026)
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="vigente"
                    checked={formData.vigente}
                    onChange={(e) =>
                      setFormData({ ...formData, vigente: e.target.checked })
                    }
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <label
                    htmlFor="vigente"
                    className="text-sm font-medium text-gray-700"
                  >
                    Curso vigente
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setMostrarModalCrear(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={creando || aniosEscolares.length === 0}
                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {creando ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Creando...
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        Crear Curso
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Notificación */}
      {notification && (
        <Notification
          isOpen={notification.isOpen}
          type={notification.type}
          title={notification.title}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </ProfesorLayout>
  );
};
